import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectMerchantCustomizations, showNotifier } from 'containers/app/appSlice';
import CreateEstimate from './create-edit-estimate/CreateEstimate';
import EstimateBuyersPreview from './buyers-preview/EstimateBuyersPreview';
import { NotifierTypes } from 'constants/notifierConstants';
import routesPath from 'routes/RoutesPath';
import { useCheckEstimateReferenceNumberMutation, useCreateEstimateMutation, useEditEstimateMutation } from './api';
import EstimateCreationConfirmationPopup from './components/EstimateCreationConfirmationPopup';
import EstimateErrorsPopup from './components/EstimateErrorsPopup';
import { ESTIMATE_ATTRIBUTE_TYPE, EstimatePageState } from './constants';
import {
  selectCascadingRelatedEntityData,
  selectEstimateReferenceNumber,
  selectInitialEstimateReferenceNumber
} from './estimatesSlice';
import { generateCreateEstimatePayload, hasDataChangesInEstimate, validateEstimateFields } from './utils';
import { useLazyGetTeamProfileDataDataQuery } from 'containers/team-profile/api';
import { CrossIconvector } from 'assets/icons';
import { GenericModal } from 'components';
import { logAnalyticEvent } from 'utils/analytics';
import { EstimatesCleverTapEvents } from './events';

const CreateEditEstimate: FC = () => {
  const { estimateId } = useParams<{ estimateId: string }>();
  const location = useLocation();
  const isEdit = location.pathname.includes('/edit');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<EstimatePageState>(EstimatePageState.CREATE);
  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showEstimateRefConfirmationPopup, setShowEstimateRefConfirmationPopup] = useState(false);
  const [isEditEstimatInProgress, setIsEditEstimateInProgress] = useState(false);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const selectedEstimateReferenceNumber = useAppSelector(selectEstimateReferenceNumber);
  const initialEstimateReferenceNumber = useAppSelector(selectInitialEstimateReferenceNumber);
  const cascadingRelatedEntityData = useAppSelector(selectCascadingRelatedEntityData);
  const { isFromInvoice, isFromJob, jobNumber, invoiceNumber } = cascadingRelatedEntityData ?? {};
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);
  const [getTeamProfileDataDataQuery, { data: teamProfileData }] = useLazyGetTeamProfileDataDataQuery();

  const [createEstimate, { isLoading: isCreateEstimateInProgress }] = useCreateEstimateMutation();
  const [editEstimate, { isLoading: isEditEstimateInProgress }] = useEditEstimateMutation();
  const [checkEstimateRefNumberQuery, { data: estimateReferenceNumberCheckResponse }] =
    useCheckEstimateReferenceNumberMutation();

  const [showEstimateErrorsPopup, setShowEstimateErrorsPopup] = useState<{ open: boolean; errors: any }>({
    open: false,
    errors: {}
  });

  useEffect(() => {
    if (isEdit) {
      logAnalyticEvent(EstimatesCleverTapEvents.webEstimateEditPageView);
    } else {
      logAnalyticEvent(EstimatesCleverTapEvents.webEstimateCreatePageView);
    }
  }, []);

  useEffect(() => {
    if (currentTeam?.id) {
      getTeamProfileDataDataQuery({ teamId: currentTeam?.id.toString() });
    }
  }, [currentTeam]);

  const checkEstimateReferenceNumber = async (value: string) => {
    const result = await checkEstimateRefNumberQuery({
      teamId: currentTeam.id,
      body: { value, attribute: ESTIMATE_ATTRIBUTE_TYPE.REFERENCE_NUMBER }
    }).unwrap();
    return result;
  };

  const handleGoBack = () => {
    setPageState(EstimatePageState.CREATE);
  };

  const handlePreview = () => {
    const isPageIncomplete = handleEstimateValidations();
    if (isPageIncomplete) {
      return;
    }
    setPageState(EstimatePageState.PREVIEW);
  };

  const handleEstimateValidations = (): boolean => {
    const { isPageIncomplete, errors } = validateEstimateFields();

    if (isPageIncomplete) {
      setShowEstimateErrorsPopup({ open: true, errors });
    }
    return isPageIncomplete;
  };

  const handleSave = async () => {
    const isPageIncomplete = handleEstimateValidations();
    if (isPageIncomplete) {
      return;
    }
    const estimateRefNumber = selectedEstimateReferenceNumber.trim();
    if (estimateRefNumber && estimateRefNumber !== initialEstimateReferenceNumber.trim()) {
      const { data } = await checkEstimateReferenceNumber(estimateRefNumber);
      const { isExists } = data ?? {};
      if (isExists) {
        setShowEstimateRefConfirmationPopup(true);
        return;
      }
    }
    if ((isFromJob || isFromInvoice) && hasDataChangesInEstimate()) {
      setShowSyncConfirmationPopup(true);
      return;
    }
    handleSaveEstimate();
  };

  const handleSaveEstimateAfterRefNumberConfirmation = () => {
    if ((isFromJob || isFromInvoice) && hasDataChangesInEstimate()) {
      setShowSyncConfirmationPopup(true);
      return;
    }
    handleSaveEstimate();
  };

  const handleSaveEstimate = async () => {
    setShowEstimateRefConfirmationPopup(false);
    try {
      let result;
      if (isEdit) {
        const estimateData = generateCreateEstimatePayload();
        result = await editEstimate({ teamId: currentTeam.id, estimateId, payload: estimateData }).unwrap();
      } else {
        const estimateData = generateCreateEstimatePayload();
        result = await createEstimate({ teamId: currentTeam.id, payload: estimateData }).unwrap();
      }

      if (result.data.ID || result.data.estimateId) {
        if (location.state?.returnTo) {
          navigate(location.state.returnTo, {
            state: location.state.returnToState || null,
            replace: true
          });
        } else {
          navigate(`${routesPath.ESTIMATES}?shareEstimate=${result.data.ID || result.data.estimateId}`);
        }
        dispatch(
          showNotifier({
            message: {
              primaryMessage: `Estimate ${isEdit ? 'updated' : 'created'} successfully!`,
              secondaryMessage: '',
              isMessageHtml: false
            },
            type: NotifierTypes.SUCCESS,
            showClose: true
          })
        );
      }
    } catch (error) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: `Failed to ${isEdit ? 'update' : 'create'} estimate. Please try again.`,
            secondaryMessage: '',
            isMessageHtml: false
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
    }
  };

  const getSyncPopupTitleAndSubtitle = (type: 'title' | 'subtitle') => {
    const title = `Save and sync changes to the related ${isFromJob || jobNumber ? 'job' : ''} ${
      (isFromJob || jobNumber) && (isFromInvoice || invoiceNumber) ? 'and' : ''
    } ${isFromInvoice || invoiceNumber ? 'invoice' : ''}?`;
    const subtitle = `Changes in your estimate will be applied to the associated ${
      isFromJob || jobNumber ? `job ${jobNumber}` : ''
    } ${(isFromJob || jobNumber) && (isFromInvoice || invoiceNumber) ? 'and' : ''} ${
      isFromInvoice || invoiceNumber ? `invoice ${invoiceNumber}` : ''
    } as well.`;
    return type === 'title' ? title : subtitle;
  };

  if (pageState === EstimatePageState.PREVIEW) {
    return (
      <EstimateBuyersPreview
        goBack={handleGoBack}
        getTeamProfileData={teamProfileData}
        onCreateAndShare={handleSave}
        isCreating={false}
      />
    );
  }

  return (
    <>
      {pageState === EstimatePageState.CREATE && (
        <CreateEstimate
          onPreview={handlePreview}
          onCreateAndShare={handleSave}
          isSaveInProgress={isCreateEstimateInProgress || isEditEstimateInProgress}
          isEditEstimateInProgress={isEditEstimatInProgress}
          setIsEditEstimateInProgress={setIsEditEstimateInProgress}
        />
      )}

      {showEstimateErrorsPopup.open && (
        <EstimateErrorsPopup
          onClose={() => setShowEstimateErrorsPopup({ open: false, errors: {} })}
          errorFields={showEstimateErrorsPopup.errors}
          maximumAmount={+merchantCustomisations?.maximumAmount || 0}
          minimumAmount={+merchantCustomisations?.minimumAmount || 0}
          onContinue={() => setShowEstimateErrorsPopup({ open: false, errors: {} })}
        />
      )}
      {showEstimateRefConfirmationPopup && (
        <EstimateCreationConfirmationPopup
          isSaveItemLoading={isCreateEstimateInProgress || isEditEstimateInProgress}
          onContinue={handleSaveEstimateAfterRefNumberConfirmation}
          closeSaveConfirmation={() => setShowEstimateRefConfirmationPopup(false)}
          estimateRefNumber={selectedEstimateReferenceNumber}
          conflictingItems={estimateReferenceNumberCheckResponse?.data?.estimateNumber}
        />
      )}

      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={getSyncPopupTitleAndSubtitle('title')}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={getSyncPopupTitleAndSubtitle('subtitle')}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleSaveEstimate}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => (
            <CrossIconvector
              height={18}
              width={18}
              title="sync-popup-close"
              className="cursor-pointer"
              onClick={() => setShowSyncConfirmationPopup(false)}
            />
          )}
          isActionInProgress={isCreateEstimateInProgress || isEditEstimateInProgress}
          height="min-h-[262px]"
          className="!px-5 !py-7 !pb-5"
        />
      )}
    </>
  );
};

export default CreateEditEstimate;
